import Phaser from "phaser";
import { WOLF_FEEL_CONFIG } from "../wolfFeelConfig";
import { WOLF_REALISM_CONFIG, type WolfMovementStateId } from "../wolfRealismConfig";

export interface WolfMovementStateInput {
  dt: number;
  elapsed: number;
  progress: number;
  danger: number;
  rearThreat: number;
  musicIntensity: number;
  threatDistancePx: number;
  hasForwardIntent: boolean;
  isChampion: boolean;
}

export interface WolfMovementFeel {
  state: WolfMovementStateId;
  stamina: number;
  maxSpeed: number;
  acceleration: number;
  deceleration: number;
  lateralSpeed: number;
  velocitySmooth: number;
  rotationLerp: number;
  rotationMaxDegrees: number;
  animationFpsMultiplier: number;
  worldSpeedMultiplier: number;
}

export class WolfStaminaSystem {
  private state: WolfMovementStateId = "longChase";
  private stateTime = 0;
  private stamina: number = WOLF_REALISM_CONFIG.maxStamina;
  private sprintCooldown = 0;

  reset() {
    this.state = "longChase";
    this.stateTime = 0;
    this.stamina = WOLF_REALISM_CONFIG.maxStamina;
    this.sprintCooldown = 0;
  }

  update(input: WolfMovementStateInput): WolfMovementFeel {
    this.sprintCooldown = Math.max(0, this.sprintCooldown - input.dt);
    this.stateTime += input.dt;

    const next = this.resolveState(input);
    if (next !== this.state) {
      this.state = next;
      this.stateTime = 0;
      if (next === "exhausted") this.sprintCooldown = WOLF_REALISM_CONFIG.sprintCooldownSeconds;
    }

    this.updateStamina(input);
    return this.currentFeel;
  }

  get currentFeel(): WolfMovementFeel {
    const stateConfig = WOLF_REALISM_CONFIG.states[this.state];
    const speedPx = this.getStateSpeedPx();

    return {
      state: this.state,
      stamina: this.stamina,
      maxSpeed: this.state === "longChase" ? WOLF_FEEL_CONFIG.maxSpeed : speedPx,
      acceleration: WOLF_FEEL_CONFIG.acceleration * stateConfig.accelerationMultiplier,
      deceleration: WOLF_FEEL_CONFIG.deceleration * stateConfig.decelerationMultiplier,
      lateralSpeed: stateConfig.lateralSpeed,
      velocitySmooth: WOLF_FEEL_CONFIG.velocitySmooth * stateConfig.velocitySmoothMultiplier,
      rotationLerp: WOLF_FEEL_CONFIG.rotationLerp * stateConfig.rotationLerpMultiplier,
      rotationMaxDegrees: WOLF_FEEL_CONFIG.rotationMaxDegrees * stateConfig.rotationMaxDegreesMultiplier,
      animationFpsMultiplier: stateConfig.animationFpsMultiplier,
      worldSpeedMultiplier: stateConfig.worldSpeedMultiplier
    };
  }

  private resolveState(input: WolfMovementStateInput): WolfMovementStateId {
    if (this.state === "exhausted") {
      return this.stateTime >= WOLF_REALISM_CONFIG.exhaustedSeconds && this.stamina >= WOLF_REALISM_CONFIG.sprintMinStamina * 0.45
        ? "longChase"
        : "exhausted";
    }

    if (this.state === "sprint") {
      const sprintConfig = WOLF_REALISM_CONFIG.states.sprint;
      if (this.stamina <= 0 || this.stateTime >= sprintConfig.maxDurationSeconds) return "exhausted";
      if (!this.shouldSprint(input) && this.stateTime > 1.1) return "longChase";
      return "sprint";
    }

    if (this.shouldSprint(input)) return "sprint";

    const canRest = input.danger <= WOLF_REALISM_CONFIG.recovery.lowThreatThreshold && input.rearThreat <= 0.18 && !input.hasForwardIntent;
    return canRest && input.elapsed < 8 ? "exploration" : "longChase";
  }

  private shouldSprint(input: WolfMovementStateInput) {
    if (input.isChampion) return false;
    if (this.sprintCooldown > 0 || this.stamina < WOLF_REALISM_CONFIG.sprintMinStamina) return false;
    const sprintConfig = WOLF_REALISM_CONFIG.states.sprint;
    const inThreatBand = input.threatDistancePx <= sprintConfig.activationDistanceMaxPx;
    const closePressure = input.threatDistancePx <= sprintConfig.activationDistanceMinPx && input.rearThreat > 0.5;
    const highTension = input.progress > 0.62 || input.musicIntensity > 0.68 || input.danger > 0.64;
    return inThreatBand && highTension && (closePressure || input.danger > 0.7);
  }

  private updateStamina(input: WolfMovementStateInput) {
    const stateConfig = WOLF_REALISM_CONFIG.states[this.state];
    const recoveryAllowed = this.state === "exploration" || this.state === "exhausted" || !WOLF_REALISM_CONFIG.recovery.recoverOnlyInExplorationOrIdle;
    const delta = recoveryAllowed ? stateConfig.staminaRecoveryPerSecond : -stateConfig.staminaDrainPerSecond;
    this.stamina = Phaser.Math.Clamp(this.stamina + delta * input.dt, 0, WOLF_REALISM_CONFIG.maxStamina);
  }

  private getStateSpeedPx() {
    if (this.state === "sprint") {
      return Phaser.Math.Linear(WOLF_REALISM_CONFIG.states.sprint.minSpeedPx, WOLF_REALISM_CONFIG.states.sprint.maxSpeedPx, 0.48);
    }
    return WOLF_REALISM_CONFIG.states[this.state].speedPx;
  }
}
